import React, { useState } from 'react'

import SettingsHeading from './SettingsHeading'
import styles from './styles.module.scss'

// SlothfulChat fork: emoji-set picker. The browser edition's runtime
// (packages/web-app/src/runtime.ts) exposes window.__slothfulchatEmoji and
// applies the chosen font stack to the --emojifonts CSS variable, which switches
// emoji everywhere at once (messages, composer, and the native-rendered
// emoji-mart picker). Electron never sets that global, so this renders nothing
// there — no target gate needed.
type EmojiSetHook = {
  sets: { id: string; label: string; note?: string }[]
  fontsFor: (id: string) => string
  current: () => string
  apply: (id: string) => void
}

// A few emoji whose look differs most between sets, for the live preview.
// Keep these emoji-presentation-default (no U+FE0F variation selector): a
// VS16 emoji like ❤️ forces color presentation, so browsers render it from a
// colour font even in the monochrome set, breaking its preview.
const PREVIEW = ['😀', '👍', '🎉', '🐢']

export default function EmojiSetSelector() {
  const hook = (window as any).__slothfulchatEmoji as EmojiSetHook | undefined
  const [current, setCurrent] = useState(() => hook?.current())
  if (!hook) {
    return null
  }

  const select = (id: string) => {
    hook.apply(id)
    setCurrent(id)
  }

  return (
    <>
      <SettingsHeading>
        Emoji style
        <span
          className={styles.forkBadge}
          role='img'
          aria-label='SlothfulChat feature'
          title='Added by SlothfulChat — not part of upstream Delta Chat'
        >
          🦥
        </span>
      </SettingsHeading>
      <div
        role='radiogroup'
        aria-label='Emoji style'
        style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
      >
        {hook.sets.map(({ id, label, note }) => {
          const selected = current === id
          return (
            <button
              type='button'
              key={id}
              role='radio'
              aria-checked={selected}
              onClick={() => select(id)}
              style={{
                // fixed preview column so every label lines up in a column
                display: 'grid',
                gridTemplateColumns: '188px 1fr',
                alignItems: 'center',
                gap: '12px',
                width: '100%',
                padding: '8px 12px',
                border: '1px solid var(--separatorColor, #8886)',
                borderRadius: '8px',
                cursor: 'pointer',
                textAlign: 'left',
                color: 'inherit',
                font: 'inherit',
                background: selected
                  ? 'var(--selectedChatBg, rgba(128,128,128,0.15))'
                  : 'transparent',
                outline: selected
                  ? '2px solid var(--colorPrimary, #415e6b)'
                  : 'none',
              }}
            >
              {/* Preview rendered in this set's own font (only downloads that
                  set's font, and only while this settings page is open). */}
              <span
                aria-hidden='true'
                style={{
                  fontFamily: hook.fontsFor(id),
                  fontSize: '20px',
                  lineHeight: 1,
                  display: 'grid',
                  gridTemplateColumns: `repeat(${PREVIEW.length}, 1fr)`,
                }}
              >
                {PREVIEW.map((e, i) => (
                  <span key={i} style={{ textAlign: 'center', overflow: 'hidden' }}>
                    {e}
                  </span>
                ))}
              </span>
              <span style={{ display: 'flex', flexDirection: 'column' }}>
                <span>{label}</span>
                {note && (
                  <span style={{ opacity: 0.7, fontSize: '0.85em' }}>{note}</span>
                )}
              </span>
            </button>
          )
        })}
      </div>
    </>
  )
}
